import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { X } from 'lucide-react';
import referralService from '../lib/referralService';

interface ExitPopupProps {
  prospectId: string;
  onClose: () => void;
  onSubmit: (referredEmail: string) => void;
}

/**
 * Composant d'exit pop-up qui apparaît lorsque l'utilisateur tente de quitter la page
 * Permet de rediriger l'analyse vers un collègue plus approprié
 */
const ExitPopup: React.FC<ExitPopupProps> = ({ prospectId, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  
  // Gestion de la fermeture du popup lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Validation de l'email
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation de l'email
    if (!email) {
      setError('Veuillez entrer une adresse email');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Vérifier si l'utilisateur est authentifié
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Si l'utilisateur n'est pas authentifié, on utilise uniquement les métadonnées
      }

      // Récupérer les informations du prospect
      const { data: prospect, error: prospectError } = await supabase
        .from('prospects')
        .select('*')
        .eq('id', prospectId)
        .single();
      
      if (prospectError || !prospect) {
        throw new Error('Impossible de récupérer les informations du prospect');
      }
      
      // Mettre à jour les métadonnées du prospect
      const updatedMetadata = {
        ...prospect.metadata,
        referred_email: email,
        referred_at: new Date().toISOString()
      };
      
      const { error: updateError } = await supabase
        .from('prospects')
        .update({ metadata: updatedMetadata })
        .eq('id', prospectId);
      
      if (updateError) {
        console.error('Erreur lors de la mise à jour des métadonnées:', updateError);
        throw new Error(`Impossible de mettre à jour les informations du prospect: ${updateError.message}`);
      }
      
      // Utiliser le service referral pour créer une redirection
      try {
        await referralService.createReferral({
          prospect_id: prospectId,
          original_email: prospect.email || prospect.metadata?.email,
          referred_email: email,
          status: 'pending'
        });
      } catch (referralError) {
        console.error('Erreur lors de la création de la redirection avec le service:', referralError);
        // On continue même si l'enregistrement dans referrals échoue
        // car les métadonnées ont été mises à jour avec succès
      }
      
      // Même si l'enregistrement dans referrals a échoué, on considère que c'est un succès
      // car les métadonnées ont été mises à jour
      setSuccess(true);
      onSubmit(email);
      
      // Fermer le popup après 3 secondes
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        ref={popupRef}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Fermer"
        >
          <X size={24} />
        </button>
        
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Vous partez déjà ?</h2>
          <p className="text-gray-600 mt-2">
            Si vous n'êtes pas la personne responsable du marketing/communication,
            indiquez-nous qui contacter dans votre entreprise.
          </p>
        </div>
        
        {!success ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email du responsable marketing/communication
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@entreprise.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>
            
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 rounded-md text-white font-medium ${
                  isSubmitting 
                    ? 'bg-blue-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center">
            <div className="mb-4 text-green-600 flex justify-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-12 w-12" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-800">Merci !</p>
            <p className="text-gray-600">
              Nous contacterons {email} prochainement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitPopup;
